import { Order, OrderStatus } from '../models/order.models';
import {
  PaymentMethodCode,
  normalizePaymentMethodValue,
  resolveBuiltInPaymentMethodCode,
} from '../../shared/types/payment-method.enum';

/**
 * Order states in which an operator may still correct the license plate (CWS-1384).
 *
 * A plate on a canceled order is a record of what was booked, and one on an order
 * mid-wash is what the lane is acting on right now; neither is a field to edit. The
 * three states here are the ones where a correction still changes something useful:
 * the wash has not started, or it finished and the receipt is wrong.
 */
export const PLATE_EDITABLE_ORDER_STATUSES: readonly string[] = [
  'pending',
  'scheduled',
  'completed',
];

/**
 * Whether the plate may be edited for an order in this status. Accepts the raw
 * `order.status.value`; an unknown or missing status is treated as not editable, so a
 * state added later has to be opted in deliberately rather than inherited by accident.
 */
export function isPlateEditableForOrderStatus(status: string | null | undefined): boolean {
  const normalized = String(status ?? '')
    .trim()
    .toLowerCase();
  return PLATE_EDITABLE_ORDER_STATUSES.includes(normalized);
}

/**
 * Utility functions for order-related operations
 */
export class OrderUtils {
  /**
   * Check if an order is a membership order
   */
  static isMembershipOrder(order: Order): boolean {
    return (
      order.washType?.toLowerCase().includes('membership') ||
      order.washType?.toLowerCase().includes('monthly') ||
      order.washType?.toLowerCase().includes('unlimited')
    );
  }

  /**
   * Check if an order is a single wash order
   */
  static isSingleWashOrder(order: Order): boolean {
    return order.washType?.toLowerCase().includes('single wash');
  }

  /**
   * Get transaction type from order
   */
  static getTransactionType(order: Order): 'Single' | 'Membership' {
    if (order.transactionType) {
      return order.transactionType as 'Single' | 'Membership';
    }

    return this.isMembershipOrder(order) ? 'Membership' : 'Single';
  }

  /**
   * Transaction type label for Order Summary (includes rewash variants).
   */
  static getOrderSummaryTransactionType(order: Order | null, washTypeRaw?: string | null): string {
    const washType = washTypeRaw || order?.washTypeValue;
    if (washType === 'rewash_single_wash') {
      return 'Rewash - Single';
    }
    if (washType === 'rewash_membership') {
      return 'Rewash - Membership';
    }
    if (washType === 'rewash_prepaid_redemption') {
      return 'Rewash - Prepaid';
    }
    if (order) {
      return this.getTransactionType(order);
    }
    return 'Single';
  }

  /** Display text for rewash reason (preset label or custom text from API). */
  static getRewashReasonDisplay(reasonRaw?: string | null, reasonDisplay?: string | null): string {
    const display = reasonDisplay?.trim();
    if (display) {
      return display;
    }
    const raw = reasonRaw?.trim();
    return raw || '-';
  }

  /** True when the order was created from a Way pass redemption. */
  static isWayRedeemedOrder(orderLike: { way_pass_id?: string | null }): boolean {
    return Boolean(String(orderLike.way_pass_id ?? '').trim());
  }

  /** List price column: Way-redemption orders show "-" (no SaaS list price). */
  static formatListPriceDisplay(orderLike: {
    list_price?: string | number | null;
    way_pass_id?: string | null;
  }): string {
    if (this.isWayRedeemedOrder(orderLike)) {
      return '-';
    }
    const raw = orderLike.list_price;
    if (raw === undefined || raw === null || String(raw).trim() === '') {
      return '-';
    }
    return `$${raw}`;
  }

  /**
   * Format price based on transaction type
   */
  static formatPrice(order: Order): string {
    if (!order.price) return '';

    if (this.isMembershipOrder(order)) {
      /*
       * CWS-1444: after a plan change, `price` (orders.final_price) is what was
       * charged at purchase and is no longer what the member is billed each
       * month — a membership downgraded from $25 to $10 kept reading
       * "$25.00/month". final_price is deliberately left alone, because it is
       * the revenue record for the period it was sold in; only the figure
       * labelled "/month" follows the current plan.
       */
      return `${order.currentPlanPrice || order.price}/month`;
    }

    if (this.isSingleWashOrder(order)) {
      return `${order.price}`;
    }

    return order.price;
  }

  /**
   * Get order status color class
   */
  static getStatusColorClass(order: Order): string {
    return `status-${order.status.color}`;
  }

  /**
   * Check if order can be cancelled
   * Only in-progress and pending orders can be cancelled
   */
  static canBeCancelled(order: Order): boolean {
    const statusValue = order.status.value.toLowerCase();
    const cancellableStatuses = ['in_progress', 'in progress', 'pending', 'scheduled', 'completed'];
    return cancellableStatuses.includes(statusValue);
  }

  /**
   * Check if order is active (scheduled or in progress)
   */
  static isActiveOrder(order: Order): boolean {
    const activeStatuses = ['scheduled', 'in_progress'];
    return activeStatuses.includes(order.status.value.toLowerCase());
  }

  /**
   * Check if order is completed
   */
  static isCompletedOrder(order: Order): boolean {
    return order.status.value.toLowerCase() === 'completed';
  }

  /**
   * Check if order is pending
   */
  static isPendingOrder(order: Order): boolean {
    return order.status.value.toLowerCase() === 'pending';
  }

  /**
   * Generate a unique barcode for order (if not present)
   */
  static generateBarcode(order: Order): string {
    return order.barCode || `BC-${order.orderId}-${order.id.slice(-6)}`;
  }

  /**
   * Calculate days since order was placed
   */
  static getDaysSinceOrder(order: Order): number {
    try {
      const orderDate = new Date(order.dateTime.replace(',', ''));
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - orderDate.getTime());
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    } catch (error) {
      return 0;
    }
  }

  /**
   * Get order summary for display
   */
  static getOrderSummary(order: Order): string {
    const type = this.getTransactionType(order);
    const vehicle = order.vehicleInfo || 'Unknown Vehicle';
    return `${type} - ${vehicle}`;
  }

  /**
   * Extract vehicle name from vehicleInfo (removes license plate part)
   */
  static getVehicleName(order: Order): string {
    const { vehicleInfo } = order;
    if (!vehicleInfo) return 'Unknown Vehicle';
    const parenIndex = vehicleInfo.indexOf('(');

    if (parenIndex !== -1) {
      return vehicleInfo.substring(0, parenIndex).trim();
    }

    return vehicleInfo;
  }

  /**
   * Get VIN number with fallback
   */
  static getVinNumber(order: Order): string {
    return order.vinNumber || 'Not Available';
  }

  /**
   * Get family plan vehicle status
   */
  static getFamilyPlanStatus(order: Order): string {
    if (order.isFamilyPlanVehicle !== undefined) {
      return order.isFamilyPlanVehicle ? 'Yes' : 'No';
    }
    return 'Not Specified';
  }

  /**
   * Check if vehicle has VIN number
   */
  static hasVinNumber(order: Order): boolean {
    return !!order.vinNumber;
  }

  /**
   * Generate vehicle display string
   */
  static getVehicleDisplayString(order: Order): string {
    const name = this.getVehicleName(order);
    const plate = order.licensePlate;
    return plate ? `${name} (${plate})` : name;
  }

  /**
   * Map API vehicle fields to UI vehicleInfo and licensePlate.
   * Fleet orders use fleet_vehicle_type / fleet_license_plate instead of make / model / license_plate.
   */
  static mapVehicleFromApi(apiOrder: {
    make?: string | null;
    model?: string | null;
    license_plate?: string | null;
    display_license_plate?: string | null;
    fleet_license_plate?: string | null;
    fleet_vehicle_type?: string | null;
    fleet_id?: string | null;
    customer_type?: string | null;
  }): { vehicleInfo: string; licensePlate: string } {
    const licensePlate = (
      apiOrder.display_license_plate ||
      apiOrder.fleet_license_plate ||
      apiOrder.license_plate ||
      ''
    )
      .toString()
      .trim();

    const isFleet = apiOrder.customer_type === 'fleet' || !!apiOrder.fleet_id;

    if (isFleet) {
      const rawType = (apiOrder.fleet_vehicle_type || '').trim();
      const typeLabel = rawType
        ? rawType.charAt(0).toUpperCase() + rawType.slice(1).toLowerCase()
        : 'Fleet vehicle';
      const vehicleInfo = licensePlate ? `${typeLabel} (${licensePlate})` : typeLabel;
      return { vehicleInfo, licensePlate };
    }

    const make = (apiOrder.make || '').toString().trim();
    const model = (apiOrder.model || '').toString().trim();
    if (make || model) {
      const name = [make, model].filter(Boolean).join(' ');
      const vehicleInfo = licensePlate ? `${name} (${licensePlate})` : name;
      return { vehicleInfo, licensePlate };
    }

    if (licensePlate) {
      return { vehicleInfo: licensePlate, licensePlate };
    }

    return { vehicleInfo: '', licensePlate: '' };
  }

  /** Resolve lane display name from API order lane fields. */
  static mapLaneNameFromApi(apiOrder: {
    lane_name?: string | null;
    lane?: string | { name?: string } | null;
    entry_lane?: string | null;
  }): string {
    const laneName = (apiOrder.lane_name || '').toString().trim();
    if (laneName) return laneName;

    const lane = apiOrder.lane;
    if (typeof lane === 'string' && lane.trim()) return lane.trim();
    if (lane && typeof lane === 'object' && lane.name?.trim()) return lane.name.trim();

    const entryLane = (apiOrder.entry_lane || '').toString().trim();
    return entryLane;
  }

  /**
   * Map raw API order/payment status to a display-ready OrderStatus object.
   * paymentStatus is accepted for API compatibility but not used in the mapping logic.
   */
  /**
   * CWS-1883: the badge for a prepaid book sale.
   *
   * The order row's own status is `completed` from the moment payment settles — it means
   * the sale went through, not that the book is used up — so a customer who had redeemed
   * none of their washes read as Completed. The server derives the book's real state
   * (`prepaid_display_status`) from the same rule the Customers > Prepaid card uses; this
   * only names and colours it.
   *
   * Returns null for anything that is not a prepaid sale, so every other row keeps the
   * ordinary `order_status` badge.
   */
  static mapPrepaidSaleStatus(prepaidDisplayStatus?: string | null): OrderStatus | null {
    switch (prepaidDisplayStatus) {
      case 'active':
        // "Scheduled": bought, paid for, and waiting to be redeemed.
        return { label: 'Scheduled', value: 'scheduled', color: 'blue' };
      case 'completed':
        return { label: 'Completed', value: 'completed', color: 'green' };
      case 'expired':
        return { label: 'Expired', value: 'expired', color: 'gray' };
      case 'cancelled':
        return { label: 'Canceled', value: 'canceled', color: 'red' };
      case 'unpaid':
        return { label: 'Pending', value: 'pending', color: 'gray' };
      default:
        return null;
    }
  }

  static mapOrderStatus(orderStatus: string, paymentStatus?: string): OrderStatus {
    if (orderStatus === 'pending') {
      return { label: 'Pending', value: 'pending', color: 'gray' };
    } else if (orderStatus === 'scheduled') {
      return { label: 'Scheduled', value: 'scheduled', color: 'blue' };
    } else if (orderStatus === 'completed') {
      return { label: 'Completed', value: 'completed', color: 'green' };
    } else if (orderStatus === 'cancelled') {
      return { label: 'Canceled', value: 'canceled', color: 'red' };
    }
    if (orderStatus === 'inprogress') {
      return { label: 'In Progress', value: 'in_progress', color: 'in-progress' };
    }
    // CWS-1773: some lines cancelled, others still live or completed.
    if (orderStatus === 'partially_cancelled') {
      return { label: 'Partially cancelled', value: 'partially_cancelled', color: 'orange' };
    }
    // CWS-1863: a legacy value. "Refunded" is a payment status; the Stripe refund webhook used
    // to copy it into the order status, and rows written before the fix still carry it. Named
    // here so they no longer read as "Pending".
    if (orderStatus === 'refunded') {
      return { label: 'Refunded', value: 'refunded', color: 'gray' };
    }
    return { label: 'Pending', value: 'pending', color: 'gray' };
  }

  /**
   * CWS-1777 / CWS-1781: the Payment status badge. The value is derived
   * server-side from everything refunded to date; this only names and colours
   * it. Anything the server did not classify (pending, failed, …) is shown as
   * is, title-cased, in grey.
   */
  static paymentStatusBadge(
    display: string | null | undefined,
    fallbackPaymentStatus?: string | null
  ): OrderStatus {
    const value = String(display || fallbackPaymentStatus || '')
      .trim()
      .toLowerCase();
    switch (value) {
      case 'paid':
        return { label: 'Paid', value, color: 'green' };
      case 'partially_refunded':
        return { label: 'Partially refunded', value, color: 'orange' };
      case 'refunded':
        return { label: 'Refunded', value, color: 'gray' };
      case 'failed':
        return { label: 'Failed', value, color: 'red' };
      case '':
        return { label: '-', value: '', color: 'gray' };
      default:
        return {
          label: value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' '),
          value,
          color: 'gray',
        };
    }
  }

  /**
   * Convert a raw wash_type identifier to a human-readable label.
   */
  static formatWashType(washType: string): string {
    switch (washType) {
      case 'single_wash':
        return 'Single Wash';
      case 'monthly_membership':
        return 'Monthly Membership';
      case 'rewash_single_wash':
        return 'Rewash - Single Wash';
      case 'rewash_membership':
        return 'Rewash - Monthly Membership';
      case 'rewash_prepaid_redemption':
        return 'Rewash - Prepaid Redemption';
      default:
        return washType ? washType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '';
    }
  }

  static isRewashWashType(washType: string | undefined | null): boolean {
    return (
      washType === 'rewash_single_wash' ||
      washType === 'rewash_membership' ||
      washType === 'rewash_prepaid_redemption'
    );
  }

  static readonly MEMBERSHIP_LIMIT_REACHED_REASON = 'membership_limit_reached';
  /** Mirrors svc-order `MEMBERSHIP_LIMIT_REACHED_MESSAGE` (CWS-1415). */
  static readonly MEMBERSHIP_LIMIT_REACHED_FALLBACK_MESSAGE =
    'Wash limit reached for this vehicle until the membership renews. Please create a new order.';
  static readonly ALL_REDEMPTIONS_USED_MESSAGE = 'All redemptions used';

  static parseWashCountFrequency(washCountFrequency?: number | string | null): number | null {
    if (
      washCountFrequency === null ||
      washCountFrequency === undefined ||
      washCountFrequency === ''
    ) {
      return null;
    }
    const parsed =
      typeof washCountFrequency === 'number'
        ? washCountFrequency
        : parseInt(String(washCountFrequency), 10);
    return Number.isFinite(parsed) ? parsed : null;
  }

  /**
   * Matches svc-order `isUnlimitedMembershipWashLimit`: period `unlimited` or
   * frequency null / missing / <= 0 means unlimited.
   */
  static isUnlimitedMembershipWashLimit(
    washCountPeriod?: string | null,
    washCountFrequency?: number | string | null
  ): boolean {
    const period = String(washCountPeriod ?? '')
      .trim()
      .toLowerCase();
    if (period === 'unlimited') {
      return true;
    }
    const frequency = this.parseWashCountFrequency(washCountFrequency);
    return frequency === null || frequency <= 0;
  }

  private static asPlainRecord(value: unknown): Record<string, unknown> | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return null;
    }
    return value as Record<string, unknown>;
  }

  private static payloadRecords(data: unknown): Record<string, unknown>[] {
    const roots: Record<string, unknown>[] = [];
    let current = this.asPlainRecord(data);
    let depth = 0;
    while (current && depth < 3) {
      roots.push(current);
      current = this.asPlainRecord(current['data']);
      depth += 1;
    }
    return roots;
  }

  /**
   * Matches on 'wash limit reached', the fragment shared by the current copy and the
   * 'Monthly wash limit reached...' it replaced in CWS-1415. Both have to be recognised:
   * svc-order and this app deploy independently, and the old wording is already stored on
   * `lpr_entries` rows and order records, where it stays for good.
   */
  static looksLikeMembershipLimitReachedMessage(value: unknown): boolean {
    return typeof value === 'string' && value.toLowerCase().includes('wash limit reached');
  }

  static isMonthlyMembershipWashType(washType: string | undefined | null): boolean {
    const raw = String(washType ?? '')
      .trim()
      .toLowerCase();
    return raw === 'monthly_membership' || raw === 'rewash_membership';
  }

  /** Unlimited | 1 Wash | N Washes */
  static formatMembershipLimitNoun(
    washCountPeriod?: string | null,
    washCountFrequency?: number | string | null
  ): string {
    if (this.isUnlimitedMembershipWashLimit(washCountPeriod, washCountFrequency)) {
      return 'Unlimited';
    }
    const frequency = this.parseWashCountFrequency(washCountFrequency) ?? 0;
    if (frequency === 1) {
      return '1 Wash';
    }
    return `${frequency} Washes`;
  }

  static formatMonthlyPlanWashTypeLine(
    washCountPeriod?: string | null,
    washCountFrequency?: number | string | null
  ): string {
    return `Monthly-${this.formatMembershipLimitNoun(washCountPeriod, washCountFrequency)}`;
  }

  static formatMembershipWashTypeLine(
    washCountPeriod?: string | null,
    washCountFrequency?: number | string | null
  ): string {
    return `Membership-${this.formatMembershipLimitNoun(washCountPeriod, washCountFrequency)}`;
  }

  static formatPackageListWashCount(
    washTypes: string[] | null | undefined,
    washCountPeriod?: string | null,
    washCountFrequency?: number | string | null
  ): string {
    const types = Array.isArray(washTypes) ? washTypes : [];
    if (!types.includes('monthly_membership')) {
      return '—';
    }
    if (this.isUnlimitedMembershipWashLimit(washCountPeriod, washCountFrequency)) {
      return 'Unlimited';
    }
    const frequency = this.parseWashCountFrequency(washCountFrequency);
    const period = String(washCountPeriod ?? '')
      .trim()
      .toLowerCase();
    if (period === 'day') {
      return `${frequency} / day`;
    }
    return `${frequency} / month`;
  }

  static formatStaffOrderWashType(
    washType: string | undefined | null,
    limitLike?: {
      wash_count_period?: string | null;
      wash_count_frequency?: number | string | null;
    }
  ): string {
    const raw = String(washType ?? '');
    if (!this.isMonthlyMembershipWashType(raw)) {
      return this.formatWashType(raw);
    }
    const line = this.formatMembershipWashTypeLine(
      limitLike?.wash_count_period,
      limitLike?.wash_count_frequency
    );
    return this.isRewashWashType(raw) ? `Rewash - ${line}` : line;
  }

  static isMembershipWashLimitExhausted(
    available: string | number | null | undefined,
    redeemed: string | number | null | undefined
  ): boolean {
    if (available == null || available === '') {
      return false;
    }
    if (String(available).trim().toLowerCase() === 'unlimited') {
      return false;
    }
    const allowed = parseInt(String(available), 10);
    const used = parseInt(String(redeemed ?? '0'), 10) || 0;
    return Number.isFinite(allowed) && allowed > 0 && used >= allowed;
  }

  static isMembershipLimitReachedPayload(data: unknown): boolean {
    for (const row of this.payloadRecords(data)) {
      if (row['refusal_reason'] === this.MEMBERSHIP_LIMIT_REACHED_REASON) {
        return true;
      }
      if (
        this.looksLikeMembershipLimitReachedMessage(row['statusMessage']) ||
        this.looksLikeMembershipLimitReachedMessage(row['message'])
      ) {
        return true;
      }
    }
    return false;
  }

  static membershipLimitReachedMessage(data: unknown): string {
    const candidates: unknown[] = [];
    for (const row of this.payloadRecords(data)) {
      candidates.push(row['statusMessage'], row['message']);
    }
    for (const candidate of candidates) {
      if (this.looksLikeMembershipLimitReachedMessage(candidate)) {
        return String(candidate).trim();
      }
    }
    return this.MEMBERSHIP_LIMIT_REACHED_FALLBACK_MESSAGE;
  }

  /** Wash count / limit line for membership orders in list + order summary. */
  static formatMembershipWashCountDisplay(orderLike: {
    wash_count_period?: string | null;
    wash_count_frequency?: number | string | null;
    subscription_washes_count?: number | null;
  }): string {
    const redeemed = orderLike.subscription_washes_count ?? 0;
    if (
      this.isUnlimitedMembershipWashLimit(
        orderLike.wash_count_period,
        orderLike.wash_count_frequency
      )
    ) {
      return `Unlimited (${redeemed} redeemed)`;
    }
    const frequency = this.parseWashCountFrequency(orderLike.wash_count_frequency);
    return `${frequency} washes`;
  }

  static isPrepaidWashBookWashType(washType: string | undefined | null): boolean {
    return washType === 'prepaid_wash_book';
  }

  /**
   * Total washes line for a prepaid book sale in the order summary: "5 (2 redeemed)".
   * The counts come from the order detail (`prepaid_total_washes`, `prepaid_redeemed_washes`),
   * summed server-side the same way the Customers > Prepaid card counts them.
   */
  static formatPrepaidWashCountDisplay(orderLike: {
    prepaid_total_washes?: number | string | null;
    prepaid_redeemed_washes?: number | string | null;
  }): string {
    const total = Number(orderLike.prepaid_total_washes);
    if (orderLike.prepaid_total_washes == null || Number.isNaN(total)) {
      return 'N/A';
    }
    const redeemed = Number(orderLike.prepaid_redeemed_washes) || 0;
    return `${total} (${redeemed} redeemed)`;
  }

  /** Badge color token for wash type pills (orange = single, purple = membership). */
  /**
   * Chip labels for an order's add-ons, shown beside the wash type in the Orders listing
   * (CWS-1125). A detailing package and a plain add-on are both picked from the one "Add on"
   * section, so `wash_type` alone never says a wash was sold with a detailing.
   *
   * Labels match `getCheckoutAdditionalServiceBlockLabel` in the order modal. Detailing leads
   * when an order carries both, so the more specific chip is the one read first.
   */
  static formatAddOnKindLabels(addonKinds: readonly string[] | null | undefined): string[] {
    if (!Array.isArray(addonKinds) || addonKinds.length === 0) {
      return [];
    }
    const present = new Set(
      addonKinds.filter(kind => typeof kind === 'string').map(kind => kind.trim().toLowerCase())
    );
    const labels: string[] = [];
    if (present.has('detailing')) {
      labels.push('Detailing');
    }
    if (present.has('add_on')) {
      labels.push('Add-on');
    }
    return labels;
  }

  /** Chip colour for an add-on label; detailing is distinguished from a plain add-on. */
  static getAddOnLabelClass(label: string): string {
    return label === 'Detailing' ? 'wash-type-teal' : 'wash-type-grey';
  }

  static getWashTypeColor(washType: string | undefined | null): string | null {
    switch (washType) {
      case 'single_wash':
      case 'rewash_single_wash':
        return 'orange';
      case 'monthly_membership':
      case 'rewash_membership':
        return 'purple';
      // CWS-1125: an add-on sold as its own order had no colour at all, so the badge rendered
      // unstyled next to the coloured wash rows. Same grey as the add-on chip, so an add-on
      // order and an add-on chip read as the same thing.
      case 'add_on':
        return 'grey';
      default:
        return null;
    }
  }

  /**
   * Capitalize the first letter of a source string.
   * Returns 'Way.com' when no source is provided.
   */
  static capitalizeSource(source: string | undefined): string {
    if (!source) return 'Way.com';
    const lower = source.toLowerCase();
    if (lower === 'website' || lower === 'web') return 'Web';
    if (lower === 'way_web' || lower === 'way web') return 'Way Web';
    if (lower === 'way_app' || lower === 'way app') return 'Way App';
    if (lower === 'scan_and_go') return 'Scan & Go';
    return source.charAt(0).toUpperCase() + source.slice(1);
  }

  /**
   * Convert a raw payment method identifier to a human-readable label.
   */
  static formatPaymentMethod(paymentMethod: string | undefined): string {
    if (!paymentMethod) return '-';

    const canonical = resolveBuiltInPaymentMethodCode(paymentMethod) ?? paymentMethod;
    const normalized = normalizePaymentMethodValue(canonical);
    switch (normalized) {
      case PaymentMethodCode.CardPresent:
      case PaymentMethodCode.Card:
        return 'Card';
      case PaymentMethodCode.Terminal:
        return 'Terminal';
      case PaymentMethodCode.TapToPay:
      case 'tap to pay':
        return 'Tap To Pay';
      case PaymentMethodCode.Link:
        return 'Payment Link';
      case PaymentMethodCode.Cash:
        return 'Cash';
      default:
        return paymentMethod
          .replace(/_/g, ' ')
          .replace(/-/g, ' ')
          .split(/\s+/)
          .filter(Boolean)
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
    }
  }
}
